import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import segmentPropTypes from '../SegmentCollapsableCard/propTypes';
import { Accordion } from 'react-bootstrap';
import './styles.scss';
import SegmentCard from '../SegmentCollapsableCard';
import { noop } from '../propTypes';
import LoadingComponent from '../LoadingComponent';

function Segments({
    photos,
    loadingPhotos,
    segments,
    onSegmentClick,
    activeSegmentId,
    loading,
    hasMoreSegments,
    moreSegmentsCallback,
    moreSegmentsLoading,
    onEditClick,
    onDeleteClick,
    loggedInUser,
}) {
    const observer = useRef();
    const lastSegmentRef = useCallback(
        (node) => {
            if (loading) return; // if the current state of segments is loading we don't need to execute the callback
            if (observer.current) observer.current.disconnect(); // remove the old observer
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMoreSegments) {
                    moreSegmentsCallback(); // if the last node is visible, callback is invoked to fetch more segments
                }
            });
            if (node) observer.current.observe(node); // last node / segment is used as the new observer
        },
        [loading, hasMoreSegments],
    );

    return (
        <>
            <div className="col-12 h-100">
                {!loading ? (
                    segments && segments.length ? (
                        <Accordion activeKey={activeSegmentId}>
                            {segments.map(
                                (
                                    {
                                        id,
                                        creatorName,
                                        litterCount,
                                        distance,
                                        duration,
                                        date,
                                        title,
                                        segmentIntro,
                                        landInfo,
                                        mapContent,
                                        tagsContent,
                                        userId,
                                        notes,
                                    },
                                    index,
                                ) => {
                                    const segmentHasNotes =
                                        notes && notes !== '';
                                    return segments.length === index + 1 ? ( // pass a Ref to the last item
                                        <div ref={lastSegmentRef}>
                                            <SegmentCard
                                                key={`segment-last-item-${id}`}
                                                id={id}
                                                creatorName={creatorName}
                                                title={title}
                                                date={date}
                                                litterCount={litterCount}
                                                distance={distance}
                                                duration={duration}
                                                avatar={segmentIntro}
                                                mapContent={mapContent}
                                                tagsContent={tagsContent}
                                                landInfo={landInfo}
                                                onSegmentClick={() =>
                                                    onSegmentClick(id)
                                                }
                                                activeSegmentId={
                                                    activeSegmentId
                                                }
                                                photos={photos}
                                                loadingPhotos={loadingPhotos}
                                                onEditClick={onEditClick}
                                                onDeleteClick={onDeleteClick}
                                                userId={userId}
                                                loggedInUser={loggedInUser}
                                                segmentHasNotes={
                                                    segmentHasNotes
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            <SegmentCard
                                                key={`segment-${id}`}
                                                id={id}
                                                creatorName={creatorName}
                                                date={date}
                                                title={title}
                                                litterCount={litterCount}
                                                distance={distance}
                                                duration={duration}
                                                avatar={segmentIntro}
                                                mapContent={mapContent}
                                                tagsContent={tagsContent}
                                                landInfo={landInfo}
                                                onSegmentClick={() =>
                                                    onSegmentClick(id)
                                                }
                                                activeSegmentId={
                                                    activeSegmentId
                                                }
                                                photos={photos}
                                                loadingPhotos={loadingPhotos}
                                                onEditClick={onEditClick}
                                                onDeleteClick={onDeleteClick}
                                                userId={userId}
                                                loggedInUser={loggedInUser}
                                                segmentHasNotes={
                                                    segmentHasNotes
                                                }
                                            />
                                        </div>
                                    );
                                },
                            )}
                            <div style={{ position: 'relative' }}>
                                <LoadingComponent
                                    isLoading={moreSegmentsLoading}
                                />
                            </div>
                        </Accordion>
                    ) : (
                        <h4 className="text-center">No Segments found</h4>
                    )
                ) : (
                    <LoadingComponent isLoading={loading} />
                )}
            </div>
        </>
    );
}

Segments.propTypes = {
    // array of segments to display in list
    segments: PropTypes.arrayOf(
        PropTypes.shape({
            ...segmentPropTypes,
        }),
    ),
    photos: PropTypes.array,
    loadingPhotos: PropTypes.bool,
    // function to pass to SegmentsCard component
    // that exexcutes on card click / card expansion
    onSegmentClick: PropTypes.func,
    // determines which segment to expand
    activeSegmentId: PropTypes.number,
    // boolean used to determine if segments list is loading
    loading: PropTypes.bool,
    // boolean used to check if observer needs to update
    hasMoreSegments: PropTypes.bool,
    // callback used whenever the last item in the list is visible
    moreSegmentsCallback: PropTypes.func,
    // loading state of lazy loading more segments on scroll,
    // used to render loader at bottom of the screen
    moreSegmentsLoading: PropTypes.bool,
    onEditClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    loggedInUser: PropTypes.object,
};

Segments.defaultProps = {
    segments: [],
    onSegmentClick: noop,
    activeSegmentId: null,
    loading: false,
    hasMoreSegments: true,
    moreSegmentsCallback: noop,
    moreSegmentsLoading: noop,
    onEditClick: noop,
    onDeleteClick: noop,
};

export default Segments;
